import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authService } from '../services';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { KeyRound, ArrowLeft, Loader2, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Ungültiger oder fehlender Reset-Token');
    }
  }, [token]);

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Passwort muss mindestens 8 Zeichen lang sein';
    }
    if (password !== confirmPassword) {
      return 'Passwörter stimmen nicht überein';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      await authService.confirmPasswordReset(token, password);
      setSuccess(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="reset-password-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-4"
        >
          <Card className="bg-white shadow-xl border-slate-200">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Passwort geändert</CardTitle>
              <CardDescription>
                Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt anmelden.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                className="w-full"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-on-primary)'
                }}
                onClick={() => navigate('/login')}
              >
                Zur Anmeldung
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="w-full max-w-md mx-4 bg-white shadow-xl border-slate-200">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Ungültiger Link</CardTitle>
            <CardDescription>
              Dieser Reset-Link ist ungültig oder abgelaufen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/forgot-password')}
            >
              Neuen Link anfordern
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-4"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            Neues Passwort
          </h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Geben Sie Ihr neues Passwort ein
          </p>
        </div>

        <Card className="bg-white shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Passwort zurücksetzen
            </CardTitle>
            <CardDescription>
              Wählen Sie ein sicheres Passwort mit mindestens 8 Zeichen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Neues Passwort</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="new-password-input"
                    className="border-slate-200 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className="border-slate-200"
                />
              </div>

              {/* Password strength indicator */}
              <div className="space-y-1">
                <div className="flex gap-1">
                  <div className={`h-1 flex-1 rounded ${password.length >= 1 ? 'bg-red-400' : 'bg-slate-200'}`} />
                  <div className={`h-1 flex-1 rounded ${password.length >= 6 ? 'bg-yellow-400' : 'bg-slate-200'}`} />
                  <div className={`h-1 flex-1 rounded ${password.length >= 8 ? 'bg-green-400' : 'bg-slate-200'}`} />
                  <div className={`h-1 flex-1 rounded ${password.length >= 12 ? 'bg-green-600' : 'bg-slate-200'}`} />
                </div>
                <p className="text-xs text-slate-500">
                  {password.length === 0 && 'Mindestens 8 Zeichen'}
                  {password.length >= 1 && password.length < 6 && 'Zu kurz'}
                  {password.length >= 6 && password.length < 8 && 'Fast da...'}
                  {password.length >= 8 && password.length < 12 && 'Gut'}
                  {password.length >= 12 && 'Sehr stark'}
                </p>
              </div>

              <Button
                type="submit"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-on-primary)'
                }}
                className="w-full hover:opacity-90"
                disabled={loading}
                data-testid="reset-password-submit"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gespeichert...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4" />
                    Passwort speichern
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Anmeldung
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
